// LoggingErrorBoundary component for the AlphaMind IDO Frontend logging system
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { LoggingService, getLoggingService } from '../../services/logging.service';
import { LogEventType, LogCategory } from '../../types/logging.types';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class LoggingErrorBoundary extends Component<Props, State> {
  private loggingService: LoggingService;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.loggingService = getLoggingService();
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to our logging service
    this.logError(error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({
      error,
      errorInfo
    });
  }

  private logError(error: Error, errorInfo: ErrorInfo) {
    try {
      // Extract component stack information
      const componentStack = errorInfo.componentStack;
      const errorBoundary = this.getErrorBoundaryInfo();

      this.loggingService.log({
        eventType: LogEventType.ERROR,
        category: LogCategory.ERROR,
        action: 'react_error_boundary',
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name
        },
        data: {
          errorType: error.constructor.name,
          componentStack,
          errorBoundary,
          recoverable: true,
          errorTime: Date.now(),
          userAgent: navigator.userAgent,
          url: window.location.href
        }
      });

      // Also log to console in development
      if (process.env.NODE_ENV === 'development') {
        console.error('LoggingErrorBoundary caught an error:', error);
        console.error('Component stack:', componentStack);
      }

    } catch (loggingError) {
      // Fallback to console if logging fails
      console.error('Failed to log error to logging service:', loggingError);
      console.error('Original error:', error);
    }
  }

  private getErrorBoundaryInfo() {
    return {
      boundaryName: 'LoggingErrorBoundary',
      timestamp: new Date().toISOString(),
      props: Object.keys(this.props).filter(key => key !== 'children')
    };
  }

  private handleRetry = () => {
    this.loggingService.log({
      eventType: LogEventType.USER_ACTION,
      category: LogCategory.ERROR,
      action: 'error_boundary_retry',
      data: {
        previousError: this.state.error?.message,
        retryTime: Date.now()
      }
    });

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{
          padding: '20px',
          margin: '20px',
          border: '1px solid #ff6b6b',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          color: '#c92a2a'
        }}>
          <h2>Something went wrong</h2>
          <p>An unexpected error occurred. The error has been logged and our team has been notified.</p>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{ marginTop: '10px' }}>
              <summary>Error Details (Development Only)</summary>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                fontSize: '12px', 
                backgroundColor: '#f8f9fa',
                padding: '10px',
                borderRadius: '4px',
                marginTop: '10px'
              }}>
                {this.state.error.message}
                {this.state.error.stack && `\n\nStack Trace:\n${this.state.error.stack}`}
                {this.state.errorInfo?.componentStack && `\n\nComponent Stack:${this.state.errorInfo.componentStack}`}
              </pre>
            </details>
          )}

          <button
            onClick={this.handleRetry}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: '#228be6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LoggingErrorBoundary;
